import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';
import AuthSplitLayout from '@/components/auth/AuthSplitLayout';
import { RESET_PANEL } from '@/data/authPanels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  usePageMeta(PAGE_META.resetPassword);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token') || searchParams.get('token');
    if (!tokenParam) setError('Invalid reset link. Please request a new one.');
    else setToken(decodeURIComponent(tokenParam));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || loading || success) return;
    const urlParams = new URLSearchParams(window.location.search);
    let currentToken = token || urlParams.get('token');
    if (currentToken) currentToken = decodeURIComponent(currentToken).trim();
    if (!currentToken) { toast.error('Reset token not found.'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }

    isSubmittingRef.current = true; setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: currentToken, new_password: formData.password })
      });
      if (response.ok) { if (!success) { setSuccess(true); toast.success('Password reset successfully!'); } }
      else {
        const text = await response.text();
        try {
          const d = JSON.parse(text);
          if (d.detail?.includes('expired') || d.detail?.includes('Invalid') || d.detail?.includes('invalid'))
            setError('This reset link has expired or been used. Please request a new one.');
          toast.error(d.detail || 'Failed to reset password');
        } catch { toast.error(`Server error (${response.status}).`); }
      }
    } catch { toast.error('Network error.'); }
    finally { setLoading(false); isSubmittingRef.current = false; }
  };

  return (
    <AuthSplitLayout
      testId="reset-password-page"
      {...RESET_PANEL}
      topRight={
        <button
          type="button"
          className={`${shared.btn} ${shared.btnQuiet}`}
          onClick={() => navigate('/login')}
          data-testid="back-to-login"
        >
          Back to sign in
        </button>
      }
    >
      <div data-testid="reset-password-card">
          {error ? (
            <div className={shell.head}>
              <div className={`${shell.statusIcon} ${shell.statusError}`}>
                <XCircle size={36} aria-hidden="true" />
              </div>
              <h1 className={shell.title}>Link Expired</h1>
              <p className={shell.subtitle}>{error}</p>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn} ${shell.spacedTop}`}
                onClick={() => navigate('/forgot-password')}
                data-testid="request-new-link-button"
              >
                Request New Link
              </button>
            </div>
          ) : success ? (
            <div className={shell.head}>
              <div className={shell.statusIcon}>
                <CheckCircle size={36} aria-hidden="true" />
              </div>
              <h1 className={shell.title}>Password Reset!</h1>
              <p className={shell.subtitle}>
                Your password has been successfully reset. You can now sign in.
              </p>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn} ${shell.spacedTop}`}
                onClick={() => navigate('/login')}
                data-testid="go-to-login-button"
              >
                Go to Login
              </button>
            </div>
          ) : (
            <>
              <div className={shell.headStart}>
                <h1 className={shell.title}>Create New Password</h1>
                <p className={shell.subtitle}>Enter your new password below</p>
              </div>

              <form onSubmit={handleSubmit} data-testid="reset-password-form">
                <div className={formStyles.field}>
                  <label htmlFor="password" className={formStyles.label}>New Password</label>
                  <div className={formStyles.adorned}>
                    <Lock size={17} className={formStyles.adornedIcon} aria-hidden="true" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`${formStyles.control} ${formStyles.withIcon}`}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter new password"
                      required
                      minLength={8}
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      className={formStyles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  <p className={formStyles.hint}>Must be at least 8 characters</p>
                </div>

                <div className={formStyles.field}>
                  <label htmlFor="confirmPassword" className={formStyles.label}>Confirm New Password</label>
                  <div className={formStyles.adorned}>
                    <Lock size={17} className={formStyles.adornedIcon} aria-hidden="true" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`${formStyles.control} ${formStyles.withIcon}`}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      data-testid="input-confirm-password"
                    />
                    <button
                      type="button"
                      className={formStyles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                  disabled={loading}
                  data-testid="submit-reset-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
      </div>
    </AuthSplitLayout>
  );
};

export default ResetPassword;
